import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Printer, MessageCircle, AlertTriangle, Link2, Pencil } from 'lucide-react';
import toast from 'react-hot-toast';
import { useQuotes } from '../hooks/useQuotes';
import { useCatalog } from '../hooks/useCatalog';
import { useSales } from '../hooks/useSales';
import { useBusinessStore } from '../store/useBusinessStore';
import { canTransition, isQuoteEditable } from '../utils/billing';
import {
  blankLine, computeDocument, defaultValidUntil, fmtDate, isExpired, lineFromProduct, money,
  approverLabel, deliveryIsOpen, DELIVERY_LABELS, NOTIFY_LABELS, publicQuoteUrl, quoteWhatsappText, renderQuoteHtml, shownStatus, stockWarnings, waPhone, whatsappUrl
} from '../utils/quotes';
import { StatusBadge } from './QuotesPage';
import { CustomerPicker, LinesTable, ProductPicker, inputCls, labelCls } from '../components/DocumentLines';

const btn = 'text-xs font-bold px-3 py-2 rounded-xl flex items-center gap-1.5 disabled:opacity-50';
const btnGhost = `${btn} border border-seryi-bg-light text-seryi-heading`;
const btnMain = `${btn} bg-seryi-green text-seryi-bg-dark font-extrabold`;

// Estado editable de una línea guardada: lo que se escribe en los campos puede ser texto mientras se teclea.
const toLine = (item) => ({ ...item });
const snapshotOf = (fields, lines) => JSON.stringify({
  f: [fields.customer_id || '', fields.price_list_key || '', fields.valid_until || '', String(fields.notes ?? '').trim()],
  l: lines.map((l) => [l.id, l.product_id, l.sku, l.description, String(l.unit ?? ''), Number(l.quantity), Number(l.unit_price), Number(l.discount_pct || 0), Number(l.tax_rate || 0), l.tax_kind])
});

const openPrintWindow = (html) => {
  const w = window.open('', '_blank');
  if (!w) { toast.error('El navegador bloqueó la ventana. Permite las ventanas emergentes para imprimir o guardar el PDF.'); return; }
  w.document.open(); w.document.write(html); w.document.close();
  w.focus();
  setTimeout(() => w.print(), 300);
};

export default function QuoteEditorPage() {
  const { id } = useParams();
  const isNew = !id || id === 'nueva';
  const navigate = useNavigate();
  const { masterDb, activeBusinessId } = useBusinessStore();
  const business = (Array.isArray(masterDb) ? masterDb : []).find((b) => String(b.id) === String(activeBusinessId)) || null;
  const { customers, settings, loading: loadingQuotes, tableMissing, getQuote, createQuote, saveQuote, transition, newVersion, duplicateQuote, getDeliveries, requestSend, getChangeRequests, resolveChangeRequest, isReadOnlyDemo } = useQuotes({ withList: false });
  const { products, lists, priceMap, defaultKey, loading: loadingCatalog } = useCatalog();
  const { findSaleByQuote, createSaleFromQuote } = useSales({ withList: false, withCustomers: false });
  const [existingSale, setExistingSale] = useState(null);

  const [loaded, setLoaded] = useState(null); // { quote, items } guardado en la base
  const [opening, setOpening] = useState(!isNew);
  const [fields, setFields] = useState({ customer_id: null, price_list_key: null, valid_until: '', notes: '' });
  const [lines, setLines] = useState([]);
  const [snapshot, setSnapshot] = useState(snapshotOf({}, []));
  const [busy, setBusy] = useState(false);

  const apply = (data) => {
    const f = { customer_id: data.quote.customer_id, price_list_key: data.quote.price_list_key, valid_until: data.quote.valid_until || '', notes: data.quote.notes || '' };
    const l = data.items.map(toLine);
    setLoaded(data); setFields(f); setLines(l); setSnapshot(snapshotOf(f, l));
  };

  useEffect(() => {
    if (isNew) { setOpening(false); return undefined; }
    let alive = true;
    setOpening(true);
    getQuote(id).then((data) => { if (!alive) return; if (data) apply(data); setOpening(false); });
    return () => { alive = false; };
  }, [id, isNew, getQuote]);

  const quote = loaded?.quote || null;
  const status = quote?.status || 'borrador';

  // Una cotización aceptada puede tener ya su venta vigente: entonces se ofrece verla en vez de crear otra.
  const quoteId = quote?.id;
  useEffect(() => {
    setExistingSale(null);
    if (!quoteId || status !== 'aceptada') return undefined;
    let alive = true;
    findSaleByQuote(quoteId).then((s) => { if (alive) setExistingSale(s); });
    return () => { alive = false; };
  }, [quoteId, status, findSaleByQuote]);
  const [deliveries, setDeliveries] = useState([]);
  const refreshDeliveries = useCallback(async () => {
    if (!quoteId) return null;
    const rows = await getDeliveries(quoteId);
    if (rows) setDeliveries(rows);
    return rows;
  }, [quoteId, getDeliveries]);
  useEffect(() => {
    setDeliveries([]);
    if (!quoteId || !['aprobada', 'enviada'].includes(status)) return;
    refreshDeliveries();
  }, [quoteId, status, refreshDeliveries]);
  // Cambios que el cliente pidió por WhatsApp sobre esta cotización (solo importa la solicitud abierta)
  const [changeRequest, setChangeRequest] = useState(null);
  useEffect(() => {
    setChangeRequest(null);
    if (!quoteId) return undefined;
    let alive = true;
    getChangeRequests(quoteId).then((rows) => { if (alive && rows) setChangeRequest(rows.find((r) => r.status === 'nueva') || null); });
    return () => { alive = false; };
  }, [quoteId, status, getChangeRequests]);
  const lastDelivery = deliveries[0] || null;
  const sending = deliveryIsOpen(lastDelivery);
  // Mientras un envío está en la cola o saliendo, se revisa cada pocos segundos; al terminar se recarga la cotización (pasa a «enviada»).
  useEffect(() => {
    if (!sending) return undefined;
    const timer = setInterval(async () => {
      const rows = await refreshDeliveries();
      if (rows && !deliveryIsOpen(rows[0])) {
        const data = await getQuote(quoteId);
        if (data) apply(data);
      }
    }, 6000);
    return () => clearInterval(timer);
  }, [sending, lastDelivery?.id, quoteId, refreshDeliveries, getQuote]);

  const editable = !quote || isQuoteEditable(status);
  const dirty = snapshotOf(fields, lines) !== snapshot;
  const customer = customers.find((c) => c.id === fields.customer_id) || null;
  const listKey = fields.price_list_key || customer?.price_list_key || defaultKey;
  const productsById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);
  const doc = useMemo(() => computeDocument(lines), [lines]);
  const warnings = useMemo(() => (editable ? stockWarnings(lines, productsById) : []), [lines, productsById, editable]);
  const shown = quote ? shownStatus(quote) : 'borrador';
  const phone = waPhone(customer);

  const setField = (k, v) => setFields((p) => ({ ...p, [k]: v }));
  const setLine = (i, patch) => setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const pickCustomer = (c) => {
    setFields((p) => ({ ...p, customer_id: c?.id || null, price_list_key: c?.price_list_key || p.price_list_key || null }));
  };

  const addProduct = (p) => {
    setLines((prev) => {
      const same = prev.findIndex((l) => l.product_id === p.id);
      if (same >= 0) return prev.map((l, i) => (i === same ? { ...l, quantity: Number(l.quantity || 0) + 1 } : l));
      return [...prev, lineFromProduct(p, { priceMap, listKey, defaultKey, position: prev.length })];
    });
  };

  const repriceToList = (key) => {
    setLines((prev) => prev.map((l) => {
      if (!l.product_id) return l;
      const own = priceMap.get(l.product_id) || {};
      const price = own[key] ?? own[defaultKey];
      return price === undefined ? l : { ...l, unit_price: price, fallbackPrice: own[key] === undefined };
    }));
  };

  const run = async (fn) => {
    setBusy(true);
    try { return await fn(); } finally { setBusy(false); }
  };

  const save = () => run(async () => {
    const payload = { ...fields, price_list_key: fields.price_list_key || listKey || null };
    if (!quote) {
      const created = await createQuote({ ...payload, lines });
      if (created) { apply(created); toast.success('Cotización creada'); navigate(`/v2/cotizaciones/${created.quote.id}`, { replace: true }); }
      return;
    }
    const saved = await saveQuote(quote, payload, lines, loaded.items);
    if (saved) apply(saved);
  });

  const change = (to, confirmText) => run(async () => {
    if (confirmText && !window.confirm(confirmText)) return;
    const data = await transition(quote, to);
    if (data) { apply(data); toast.success(`Cotización ${to === 'borrador' ? 'vuelta a borrador' : `marcada como ${to}`}`); }
  });

  const makeVersion = () => run(async () => {
    if (!window.confirm('Se crea un borrador con una versión nueva (mismo número) y esta queda como reemplazada. ¿Continuar?')) return;
    const created = await newVersion(quote, loaded.items);
    if (created) { toast.success(`Versión ${created.quote.version} creada`); navigate(`/v2/cotizaciones/${created.quote.id}`); }
  });

  const makeSale = () => run(async () => {
    if (!window.confirm('Se crea la venta con las líneas y precios de esta cotización y se descuentan las existencias de los productos del catálogo. ¿Continuar?')) return;
    const row = await createSaleFromQuote(quote.id);
    if (row?.sale_id) navigate(`/v2/ventas/${row.sale_id}`, { state: { warnings: row.warnings || [] } });
  });

  // Atajo para el cliente que ya aceptó: marca la cotización como aceptada y crea la venta en un solo paso.
  // Si la venta no se pudo crear, la cotización queda aceptada y el botón «Crear venta» sigue disponible.
  const acceptAndSell = () => run(async () => {
    if (!window.confirm('Se marca la cotización como aceptada por el cliente y se crea la venta con sus líneas y precios; se descuentan las existencias de los productos del catálogo. ¿Continuar?')) return;
    const accepted = await transition(quote, 'aceptada');
    if (!accepted) return;
    apply(accepted);
    const row = await createSaleFromQuote(quote.id);
    if (row?.sale_id) navigate(`/v2/ventas/${row.sale_id}`, { state: { warnings: row.warnings || [] } });
  });

  const duplicate = () => run(async () => {
    const created = await duplicateQuote(quote, loaded.items);
    if (created) { toast.success(`Copia creada: ${created.quote.number}`); navigate(`/v2/cotizaciones/${created.quote.id}`); }
  });

  const printDoc = () => openPrintWindow(renderQuoteHtml({ quote, items: loaded.items, customer, business, settings }));

  const publicLink = quote?.public_token ? publicQuoteUrl(window.location.origin, quote.public_token) : '';

  const sendNow = () => run(async () => {
    const ok = await requestSend(quote);
    if (ok) { toast.success('En cola: el mensaje sale en el próximo minuto'); await refreshDeliveries(); }
  });

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicLink); toast.success('Enlace copiado'); } catch { toast.error('No se pudo copiar. Selecciónalo y cópialo a mano.'); }
  };

  const openWhatsapp = () => {
    const text = quoteWhatsappText({ quote, items: loaded.items, customer, business, settings, link: publicLink });
    window.open(whatsappUrl(phone, text), '_blank', 'noopener');
  };

  if (tableMissing) {
    return <div className="p-6 text-xs text-amber-300">Falta ejecutar la migración <code>20261003b_billing_base.sql</code> en Supabase para usar las cotizaciones.</div>;
  }
  if (opening || loadingQuotes || loadingCatalog) return <p className="text-xs text-seryi-text">Cargando…</p>;
  if (!isNew && !quote) {
    return <div className="space-y-3 text-xs text-seryi-text"><p>No se encontró la cotización.</p><Link to="/v2/cotizaciones" className="text-seryi-green font-bold">Volver a la lista</Link></div>;
  }

  const total = editable ? doc.totals : quote;
  const canSave = editable && !busy && !isReadOnlyDemo && doc.ok && lines.length > 0 && (dirty || !quote);
  const canApprove = quote && status === 'borrador' && !dirty && !busy && canTransition(status, 'aprobada') && Number(quote.total) > 0;

  return (
    <div className="space-y-5 max-w-6xl">
      <Link to="/v2/cotizaciones" className="text-[11px] text-seryi-text hover:text-seryi-heading flex items-center gap-1"><ArrowLeft size={12} /> Cotizaciones</Link>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-extrabold text-seryi-heading flex items-center gap-2 flex-wrap">
            {quote ? `Cotización ${quote.number}${quote.version > 1 ? ` · versión ${quote.version}` : ''}` : 'Nueva cotización'}
            {quote && <StatusBadge status={shown} />}
          </h2>
          {quote && (
            <p className="text-[11px] text-seryi-text mt-1">
              Creada {fmtDate(quote.created_at)}{quote.source === 'bot' ? ' por el bot' : ''}
              {quote.approved_at && ` · aprobada ${fmtDate(quote.approved_at)} por ${approverLabel(quote.approved_by)}`}
              {quote.sent_at && ` · enviada ${fmtDate(quote.sent_at)}`}
              {quote.accepted_at && ` · aceptada ${fmtDate(quote.accepted_at)}`}
            </p>
          )}
          {!quote && <p className="text-[11px] text-seryi-text mt-1">El número se asigna al guardar.</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {editable && <button type="button" disabled={!canSave} onClick={save} className={btnMain}>{busy ? 'Guardando…' : quote ? 'Guardar' : 'Crear cotización'}</button>}
          {canApprove && <button type="button" onClick={() => change('aprobada', 'Al aprobarla ya no podrás editarla (podrás volverla a borrador). ¿Aprobar esta cotización?')} className={btnMain}>Aprobar</button>}
          {quote && status === 'aprobada' && <button type="button" disabled={busy} onClick={() => change('borrador', 'Vuelve a borrador para que la edites. Si el envío por WhatsApp aún no salió, se cancela. ¿Editarla?')} className={btnGhost}><Pencil size={13} /> Editar</button>}
          {quote && status === 'enviada' && <button type="button" disabled={busy} onClick={makeVersion} className={btnGhost}><Pencil size={13} /> Editar (nueva versión)</button>}
          {quote && <button type="button" disabled={dirty} title={dirty ? 'Guarda los cambios antes de imprimir' : undefined} onClick={printDoc} className={btnGhost}><Printer size={13} /> PDF / Imprimir</button>}
        </div>
      </div>

      {isExpired(quote) && (
        <div className="p-3 rounded-xl border border-orange-500/40 bg-orange-500/10 text-xs text-orange-200 flex items-center gap-2">
          <AlertTriangle size={14} /> La fecha de vigencia ya pasó ({fmtDate(quote.valid_until)}).
          <button type="button" className="underline font-bold" onClick={() => change('vencida', '¿Marcar esta cotización como vencida?')}>Marcar como vencida</button>
        </div>
      )}
      {!editable && <div className="p-3 rounded-xl border border-seryi-bg-light text-[11px] text-seryi-text">Esta cotización ya no es un borrador y no se edita directamente. {status === 'enviada' ? 'Para cambiarla pulsa «Editar (nueva versión)»: se crea una copia en borrador con el mismo número, y al aprobarla se envía de nuevo.' : status === 'aprobada' ? 'Para cambiarla pulsa «Editar»: vuelve a borrador y la apruebas otra vez.' : ''}</div>}

      {quote && changeRequest && (
        <div className="p-3 rounded-xl border border-rose-500/40 bg-rose-500/10 text-xs text-rose-200 space-y-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <MessageCircle size={14} />
            <span className="font-bold">El cliente pidió un cambio</span>
            <span>{fmtDate(changeRequest.created_at)}</span>
            <span className="text-rose-300/80">{NOTIFY_LABELS[changeRequest.notify_status]}</span>
            {!isReadOnlyDemo && (
              <button type="button" className="ml-auto underline font-bold" onClick={async () => { if (await resolveChangeRequest(changeRequest.id)) setChangeRequest(null); }}>Marcar como atendida</button>
            )}
          </div>
          <p className="text-rose-100">«{changeRequest.request_text}»</p>
          <p className="text-[10px] text-rose-300/80">Pulsa «Editar (nueva versión)» para corregirla y reenviarla: al crear la versión nueva la solicitud se cierra sola.</p>
        </div>
      )}

      {quote && ['aprobada', 'enviada'].includes(status) && lastDelivery && lastDelivery.status !== 'cancelado' && (
        <div className={`p-3 rounded-xl border text-xs flex items-center gap-2 flex-wrap ${lastDelivery.status === 'error' ? 'border-amber-500/40 bg-amber-500/10 text-amber-200' : lastDelivery.status === 'enviado' ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200' : 'border-seryi-bg-light text-seryi-heading'}`}>
          <MessageCircle size={14} />
          <span className="font-bold">{DELIVERY_LABELS[lastDelivery.status]}</span>
          {lastDelivery.status === 'enviado' && lastDelivery.sent_at && <span>{fmtDate(lastDelivery.sent_at)}{lastDelivery.phone ? ` · +${lastDelivery.phone}` : ''}</span>}
          {lastDelivery.status === 'pendiente' && <span>{lastDelivery.attempts > 0 && lastDelivery.error ? `Reintentando: ${lastDelivery.error}` : 'Sale en el próximo minuto.'}</span>}
          {lastDelivery.status === 'error' && <span>{lastDelivery.error}</span>}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <CustomerPicker customers={customers} value={fields.customer_id} disabled={!editable} onPick={pickCustomer} />
        <div className="space-y-1">
          <label htmlFor="q-list" className={labelCls}>Lista de precios</label>
          <select id="q-list" className={inputCls} disabled={!editable || lists.length === 0} value={listKey || ''} onChange={(e) => { setField('price_list_key', e.target.value); if (lines.some((l) => l.product_id) && window.confirm('¿Actualizar los precios de las líneas a esta lista?')) repriceToList(e.target.value); }}>
            {lists.map((l) => <option key={l.key} value={l.key}>{l.name}{l.is_default ? ' (predeterminada)' : ''}</option>)}
            {lists.length === 0 && <option value="">Sin listas (crea una en el Catálogo)</option>}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="q-valid" className={labelCls}>Vigente hasta</label>
          <input id="q-valid" type="date" className={inputCls} disabled={!editable} value={fields.valid_until || ''} onChange={(e) => setField('valid_until', e.target.value)} />
          <p className="text-[10px] text-seryi-text">{fields.valid_until ? '' : `Si lo dejas vacío, al enviarla queda ${fmtDate(defaultValidUntil(settings))} (${settings.quote_valid_days} días).`}</p>
        </div>
      </div>

      {editable && (
        <ProductPicker products={products} priceMap={priceMap} listKey={listKey} defaultKey={defaultKey} onAdd={addProduct} />
      )}

      <LinesTable
        editable={editable} lines={editable ? lines : loaded.items} errors={doc.errors} lineTotals={doc.lineTotals}
        onChange={setLine} onRemove={(i) => setLines((prev) => prev.filter((_, idx) => idx !== i))}
      />
      {editable && <button type="button" onClick={() => setLines((prev) => [...prev, blankLine(prev.length)])} className={btnGhost}><Plus size={13} /> Línea libre (sin catálogo)</button>}

      {warnings.length > 0 && (
        <div className="p-3 rounded-xl border border-amber-500/40 bg-amber-500/10 text-xs text-amber-200 space-y-0.5">
          {warnings.map((w) => <p key={w.product_id}><AlertTriangle size={12} className="inline mr-1" />{w.name}: pides {w.needed} y hay {w.stock} en existencias. Cotizar no descuenta existencias.</p>)}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label htmlFor="q-notes" className={labelCls}>Notas para el cliente</label>
          <textarea id="q-notes" rows={4} className={inputCls} disabled={!editable} value={fields.notes || ''} onChange={(e) => setField('notes', e.target.value)} />
        </div>
        <div className="bg-seryi-bg border border-seryi-bg-light rounded-2xl p-4 text-xs text-seryi-heading space-y-1.5">
          <div className="flex justify-between"><span className="text-seryi-text">Subtotal</span><span>{money(total.subtotal)}</span></div>
          <div className="flex justify-between"><span className="text-seryi-text">Descuentos</span><span>−{money(total.discount_total)}</span></div>
          <div className="flex justify-between"><span className="text-seryi-text">Impuestos</span><span>{money(total.tax_total)}</span></div>
          <div className="flex justify-between text-base font-black border-t border-seryi-bg-light pt-2"><span>Total ({settings.currency})</span><span className="text-emerald-400">{money(total.total)}</span></div>
          {editable && dirty && <p className="text-[10px] text-amber-300">Hay cambios sin guardar: el total definitivo lo calcula la base al guardar.</p>}
        </div>
      </div>

      {quote && (
        <div className="flex flex-wrap gap-2 border-t border-seryi-bg-light pt-4">
          {status === 'aprobada' && (
            <>
              <button type="button" disabled={!phone || busy || sending} onClick={sendNow} className={btnMain}><MessageCircle size={13} /> {sending ? 'Enviándose…' : 'Enviar por WhatsApp'}</button>
              <button type="button" disabled={!phone} onClick={openWhatsapp} className={btnGhost}><MessageCircle size={13} /> Abrir WhatsApp con el mensaje</button>
              {publicLink && <button type="button" onClick={copyLink} className={btnGhost}><Link2 size={13} /> Copiar enlace</button>}
              <button type="button" disabled={busy} onClick={() => change('enviada', '¿Ya se la enviaste al cliente? Se marcará como enviada y se fijará la vigencia.')} className={btnGhost}>Ya la envié: marcar como enviada</button>
            </>
          )}
          {status === 'enviada' && (
            <>
              <button type="button" disabled={busy} onClick={acceptAndSell} className={btnMain}>Aceptada: convertir en venta</button>
              <button type="button" disabled={busy} onClick={() => change('aceptada', '¿El cliente aceptó esta cotización?')} className={btnGhost}>Aceptada por el cliente</button>
              <button type="button" disabled={busy} onClick={() => change('rechazada', '¿El cliente rechazó esta cotización?')} className={btnGhost}>Rechazada</button>
              <button type="button" disabled={!phone || busy || sending} onClick={sendNow} className={btnGhost}><MessageCircle size={13} /> {sending ? 'Enviándose…' : 'Reenviar por WhatsApp'}</button>
              <button type="button" disabled={!phone} onClick={openWhatsapp} className={btnGhost}><MessageCircle size={13} /> Abrir WhatsApp con el mensaje</button>
              {publicLink && <button type="button" onClick={copyLink} className={btnGhost}><Link2 size={13} /> Copiar enlace</button>}
            </>
          )}
          {status === 'aceptada' && (existingSale
            ? <Link to={`/v2/ventas/${existingSale.id}`} className={btnMain}>Ver la venta {existingSale.number}</Link>
            : <button type="button" disabled={busy} onClick={makeSale} className={btnMain}>Crear venta</button>)}
          {['aceptada', 'rechazada', 'vencida', 'reemplazada'].includes(status) && (
            <button type="button" disabled={busy} onClick={duplicate} className={btnGhost}>Duplicar como borrador nuevo</button>
          )}
          {(status === 'aprobada' || status === 'enviada') && !phone && (
            <p className="text-[11px] text-amber-300 self-center">{customer ? 'Este cliente no tiene un número de WhatsApp visible: vincúlalo en Clientes.' : 'Asigna un cliente para enviarla por WhatsApp.'}</p>
          )}
        </div>
      )}
    </div>
  );
}
